import React, { useEffect, useCallback, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { StopCardList } from './StopCardList';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { atom_selectedFilterState } from '@app/shared/state/filter.atom';
import { atom_tourState, atom_originalTourState } from '@app/shared/state/tour.atom';
import { atom_listModeState, atom_mobileListHeight } from '@app/shared/state/list-mode.atom';
import { sideBarStateAtom } from '@app/shared/state/sidebar.atom';
import { useResizablePanel } from '@app/shared/hooks/useResizablePanel';
import { useRenderTracker } from '@app/shared/debug';
import { Z_INDEX } from '@app/shared/styles/zIndex';

interface MobileListContentProps {
  isHebrew: () => boolean;
  isListMode: boolean;
  contentRef: React.RefObject<HTMLDivElement>;
  preventMapInteraction: (e: React.MouseEvent | React.TouchEvent) => void;
  flyTo: (coords: [number, number], zoom: number) => void;
  onToggleSmartAgent?: () => void;
  mapref?: React.RefObject<any>;
  flyToSelectedStop?: (stopId: string) => void;
  initiallyVisible?: boolean;
}

export const MobileListContent = React.memo<MobileListContentProps>(function MobileListContent({
  isHebrew,
  isListMode,
  contentRef,
  preventMapInteraction,
  flyTo,
  onToggleSmartAgent,
  mapref,
  flyToSelectedStop,
  initiallyVisible
}) {
  useRenderTracker('MobileListContent');
  const { t } = useTranslation();

  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
  const minHeight = Math.floor(viewportHeight * 0.25); // 25% of viewport
  const maxHeight = Math.floor(viewportHeight * 0.85); // 85% of viewport

  const [savedScrollTop, setSavedScrollTop] = useState<number | null>(null);
  const [isRerendering, setIsRerendering] = useState(false);
  // const [visibleStopIndices, setVisibleStopIndices] = useState<{ start: number; end: number } | null>(null);

  const { currentHeight, setCurrentHeight, isDragging, isMaxHeight, setIsMaxHeight, dragHandleProps, containerTransitionStyle, isMounted } = useResizablePanel({
    minHeight,
    maxHeight,
    initialHeight: minHeight + 100,
    initiallyVisible,
    onDragStart: () => {
      if (contentRef.current) {
        setSavedScrollTop(contentRef.current.scrollTop);
        // visibleStopIndices disabled — drag no longer triggers re-renders
        // const cards = contentRef.current.querySelectorAll('article[id]');
        // let firstVisibleIndex = 0;
        // for (let i = 0; i < cards.length; i++) {
        //   const card = cards[i] as HTMLElement;
        //   if (card.offsetTop + card.offsetHeight > contentRef.current!.scrollTop) {
        //     firstVisibleIndex = i;
        //     break;
        //   }
        // }
        // setVisibleStopIndices({ start: firstVisibleIndex, end: firstVisibleIndex + 5 });
      }
    },
    onDragEnd: () => {
      // setVisibleStopIndices(null);
      setIsRerendering(true);
      requestAnimationFrame(() => {
        if (savedScrollTop !== null && contentRef.current) {
          contentRef.current.scrollTop = savedScrollTop;
          setSavedScrollTop(null);
        }
        setIsRerendering(false);
      });
    },
  });

  // Import atoms directly
  const activeStopState = useRecoilValue(atom_activeStopState);
  const activeStopId = activeStopState?.id;
  const selectedFilters = useRecoilValue(atom_selectedFilterState);
  const tourState = useRecoilValue(atom_tourState);
  const setSelectedFilters = useSetRecoilState(atom_selectedFilterState);
  const setActiveStopState = useSetRecoilState(atom_activeStopState);
  const setTourState = useSetRecoilState(atom_tourState);
  const originalTour = useRecoilValue(atom_originalTourState);

  // Saved mode state
  const sideBarState = useRecoilValue(sideBarStateAtom);
  const setSideBarState = useSetRecoilState(sideBarStateAtom);
  const setListMode = useSetRecoilState(atom_listModeState);
  const isSavedMode = sideBarState === 'saved';

  // Re-publish height when this list becomes the active mode again — see
  // HeightRepublisher below for why. Subscription to atom_listModeState is
  // isolated in that child so MobileListContent itself stays unsubscribed.
  // currentHeight is exposed via a ref-getter so dragging doesn't re-render
  // the child either.
  const currentHeightRef = React.useRef(currentHeight);
  currentHeightRef.current = currentHeight;
  const getCurrentHeight = useCallback(() => currentHeightRef.current, []);

  // Applied filters count (not stops)
  const appliedFiltersCount = Array.isArray(selectedFilters)
    ? selectedFilters.filter(Boolean).length
    : 0;

  // Show filter banner when filters are applied (but not in saved mode)
  const showFilterBanner = appliedFiltersCount > 0 && !isSavedMode;

  // Show saved banner when in saved mode
  const showSavedBanner = isSavedMode;

  const clearFilters = useCallback(() => {
    setSelectedFilters([]);
    setActiveStopState(undefined);
    setTourState(originalTour);
  }, [setSelectedFilters, setActiveStopState, setTourState, originalTour]);

  // Exit saved mode by clearing sideBarState only.
  // selector_visibleStops automatically un-filters when sideBarState !== 'saved'.
  // Don't touch tourState here — originalTour may be undefined (it's only ever set
  // by FilterContent / Cube), and writing undefined to tourState crashes the app.
  const exitSavedMode = useCallback(() => {
    setSideBarState(null);
  }, [setSideBarState]);

  // Scroll selected card into view when activeStopId changes
  useEffect(() => {
    if (!activeStopId) return;
    if (!contentRef.current) return;

    // Check if scroll should be skipped (set by MapCard's skipScrollRef)
    const skipScroll = (window as any).__skipListScroll;
    if (skipScroll) {
      (window as any).__skipListScroll = false;
      return;
    }

    const cardNode = document.getElementById(activeStopId);
    if (cardNode) {
      contentRef.current.scrollTo({
        top: cardNode.offsetTop,
        behavior: 'smooth',
      });
    }
  }, [activeStopId, contentRef]);

  // Scroll to active stop when returning to this list mode
  useEffect(() => {
    if (!isListMode) return;
    if (!activeStopId) return;
    if (!contentRef.current) return;

    // Small delay to ensure DOM is ready after mode switch
    const timeoutId = setTimeout(() => {
      const cardNode = document.getElementById(activeStopId);
      if (cardNode && contentRef.current) {
        contentRef.current.scrollTo({
          top: cardNode.offsetTop,
          behavior: 'smooth',
        });
      }
    }, 30);

    return () => clearTimeout(timeoutId);
  }, [isListMode]);

  return (
    <>
      {currentHeight >= viewportHeight * 0.7 && (
        <>
          <Box
            sx={{
              position: 'fixed',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: Z_INDEX.mapButton,
              bottom: tourState?.config?.sidebar === 'false' ? '20px' : '80px',
            }}
          >
            <Button
              size="large"
              variant="contained"
              color="secondary"
              onClick={() => {
                setIsMaxHeight(false);
                const targetHeight = minHeight + 100;
                setCurrentHeight(targetHeight);

                // Also update DOM directly (drag uses DOM, not state)
                const panel = dragHandleProps.ref.current?.parentElement;
                if (panel) {
                  panel.style.transition = 'height 0.25s ease-out';
                  panel.style.height = `${targetHeight}px`;
                  // Remove transition after animation
                  setTimeout(() => { panel.style.transition = ''; }, 250);
                }

                // Scroll to active stop card so it's at the top of the shrunk list
                const targetId = activeStopId || 'id';
                const targetEl = document.getElementById(targetId);
                if (targetEl && contentRef.current) {
                  contentRef.current.scrollTo({
                    top: targetEl.offsetTop,
                    behavior: 'smooth',
                  });
                }
              }}
            >
              {t('shrink_list')}
            </Button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '4px',
              width: '32px',
              borderRadius: '8px',
              mb: 0.5
            }}
          >
            <Box
              component="svg"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="black"
              width="24px"
              height="24px"
            >
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
            </Box>
          </Box>
        </>
      )}
      <Box
        sx={{
          boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.15)',
          bgcolor: tourState?.config?.listColor ? `#${tourState.config.listColor.replace('#', '')}` : '#F6F6F6',
          position: 'fixed',
          left: 0,
          right: 0,
          zIndex: Z_INDEX.bottomSheet,
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          px: { xs: 1.5, md: 3 },
          borderRadius: '20px 20px 0px 0px',
          bottom: 0,
          height: `${currentHeight}px`,
          maxHeight: '85vh',
          touchAction: 'none',
          pointerEvents: 'auto',
          isolation: 'isolate',
          ...containerTransitionStyle,
          transform: isMounted ? 'translateZ(0) translateY(0)' : 'translateZ(0) translateY(100%)',
          backfaceVisibility: 'hidden',
        }}
        onClick={preventMapInteraction}
        onMouseDown={preventMapInteraction}
        onMouseMove={preventMapInteraction}
      >
        {/* Drag Handle - tracks finger directly */}
        <Box
          {...dragHandleProps}
          sx={{
            position: 'relative',
            zIndex: 999999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'ns-resize',
            touchAction: 'none',
            padding: '10px 0',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px 40px',
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(95,93,92,1)',
                height: '2px',
                width: '60px',
                borderRadius: '8px',
                mb: 0.5
              }}
            />
            <Box
              sx={{
                bgcolor: 'rgba(95,93,92,1)',
                height: '2px',
                width: '60px',
                borderRadius: '8px'
              }}
            />
          </Box>
        </Box>

        {/* Filter/Saved banner - outside scroll, inside resizable container */}
        {showFilterBanner && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              alignItems: 'stretch',
              zIndex: 10,
              bgcolor: tourState?.config?.listColor ? `#${tourState.config.listColor.replace('#', '')}` : '#F6F6F6',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontWeight: 600,
                gap: 1,
                width: '100%',
              }}
            >
              {(() => {
                const filterLabel = appliedFiltersCount === 1 && selectedFilters[0]
                  ? selectedFilters[0]
                  : (t('filter') || 'Filter');
                return `${filterLabel}${appliedFiltersCount ? ` (${tourState?.stops.length})` : ''}`;
              })()}
              <Button
                onClick={clearFilters}
                disabled={appliedFiltersCount === 0}
                variant="text"
                disableRipple
                sx={{
                  color: appliedFiltersCount > 0 ? '#48526B' : '#999',
                  backgroundColor: 'transparent',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '14px',
                  minWidth: 'auto',
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: appliedFiltersCount > 0 ? 'underline' : 'none',
                  },
                  '&.Mui-disabled': {
                    color: '#999',
                    backgroundColor: 'transparent',
                  }
                }}
              >
                {t('clear_all') || 'Clear all'}
              </Button>
            </Box>
          </Box>
        )}
        {showSavedBanner && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              alignItems: 'stretch',
              zIndex: 10,
              bgcolor: tourState?.config?.listColor ? `#${tourState.config.listColor.replace('#', '')}` : '#F6F6F6',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontWeight: 500,
                gap: 1,
                width: '100%',
              }}
            >
              {t('saved') || 'Saved'}
              <Button
                onClick={exitSavedMode}
                variant="text"
                disableRipple
                sx={{
                  backgroundColor: 'transparent',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '14px',
                  minWidth: 'auto',
                  p: 0,
                  color: '#48526B',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
              >
                {t('back') || 'Back'}
              </Button>
            </Box>
          </Box>
        )}

        <Box
          component="nav"
          ref={contentRef}
          role="navigation"
          aria-label={t('accessibility.tour_stops_list') as string}
          sx={{
            zIndex: 10,
            width: '100%',
            mt: 0,
            pb: '60px',
            flex: 1,
            minHeight: 0,
            overflowY: isRerendering ? 'hidden' : 'auto',
            display: 'block',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            pointerEvents: isRerendering ? 'none' : 'auto',
            touchAction: isRerendering ? 'none' : 'pan-y',
            transform: 'translateZ(0)',
            userSelect: isDragging ? 'none' : 'auto',
            WebkitUserSelect: isDragging ? 'none' : 'auto',
            opacity: (isDragging || isRerendering) ? 0.5 : 1,
            transition: 'opacity 0.05s ease-out',
            willChange: isDragging ? 'opacity' : 'auto',
          }}
          onClick={preventMapInteraction}
          onTouchStart={preventMapInteraction}
          onTouchMove={preventMapInteraction}
          onMouseDown={preventMapInteraction}
          onMouseMove={preventMapInteraction}
        >
          <StopCardList
            mode="mobile"
            isHebrew={isHebrew}
            isListMode={isListMode}
            preventMapInteraction={preventMapInteraction}
            mapref={mapref}
            flyToSelectedStop={flyToSelectedStop}
            // visibleStopIndices={visibleStopIndices}
          />
        </Box>
        {isRerendering && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1001,
              pointerEvents: 'none',
              bgcolor: 'white',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <CircularProgress
              variant="determinate"
              value={50}
              size={30}
              thickness={4}
              sx={{
                animation: 'spin 0.8s linear infinite',
                '@keyframes spin': {
                  '0%': {
                    transform: 'rotate(0deg)',
                  },
                  '100%': {
                    transform: 'rotate(360deg)',
                  },
                },
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }}
            />
          </Box>
        )}
      </Box>
      <HeightRepublisher getCurrentHeight={getCurrentHeight} />
    </>
  );
});

// Isolated subscriber: re-publishes mobileList's height to atom_mobileListHeight
// whenever listMode flips back to a base mode (mobileList / null). Necessary
// because overlay panels (filter, addPlace, etc.) call setMobileListHeight(0)
// in their unmount cleanup while MobileListContent is still mounted (hidden
// via display:none) — its own write-effect doesn't re-fire because its
// currentHeight didn't change. Without this, the atom is stuck at 0 until
// the user resizes the list panel.
//
// Subscription is local to this component so the parent stays unsubscribed
// from listMode and doesn't re-render on every overlay open/close. Height
// is read via a getter so dragging doesn't re-render this either.
const HeightRepublisher = React.memo(function HeightRepublisher({
  getCurrentHeight,
}: {
  getCurrentHeight: () => number;
}) {
  const listMode = useRecoilValue(atom_listModeState);
  const setMobileListHeight = useSetRecoilState(atom_mobileListHeight);
  useEffect(() => {
    const isActiveBase = listMode === 'mobileList' || listMode === null;
    const h = getCurrentHeight();
    if (isActiveBase && h > 0) {
      setMobileListHeight(h);
    }
  }, [listMode, getCurrentHeight, setMobileListHeight]);
  return null;
});
